import React, { useEffect, useState } from 'react';
import { getRuntimeVersion } from '../lib/api';

/**
 * Small build-freshness indicator shown in the employee console (and anywhere
 * else we want it). It answers a single question every time we open the page:
 *
 *   "Is the code that is actually serving me the code I think it is?"
 *
 * - Frontend SHA is baked in at build time via REACT_APP_GIT_SHA / BUILD_TIME.
 * - Backend SHA is fetched from the unauthenticated /api/runtime/version
 *   endpoint.
 * - When the two do not come from matching commits, we highlight it.
 *
 * Intentionally light on styling so it can drop into any page header without
 * design work.
 */

const FRONTEND_SHA_RAW = process.env.REACT_APP_GIT_SHA || '';
const FRONTEND_SHA = FRONTEND_SHA_RAW ? FRONTEND_SHA_RAW.slice(0, 7) : 'dev';
const FRONTEND_BUILD_TIME = process.env.REACT_APP_BUILD_TIME || '';

const formatTimestamp = (value) => {
  if (!value) return '';
  try {
    const asDate = new Date(value);
    if (Number.isNaN(asDate.getTime())) return value;
    return asDate.toLocaleString();
  } catch (err) {
    return value;
  }
};

const BuildBadge = ({ className = '' }) => {
  const [backend, setBackend] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRuntimeVersion();
        if (!cancelled) setBackend(res.data || null);
      } catch (err) {
        if (!cancelled) setError('unreachable');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const backendSha = backend?.sha_short || (error ? '?' : '…');
  const mismatch = Boolean(
    backend?.sha_short && FRONTEND_SHA !== 'dev' && backend.sha_short !== FRONTEND_SHA
  );

  const tooltipLines = [
    `frontend: ${FRONTEND_SHA}${FRONTEND_BUILD_TIME ? ` (built ${formatTimestamp(FRONTEND_BUILD_TIME)})` : ''}`,
    backend
      ? `backend:  ${backend.sha_short} on ${backend.branch || '?'} — ${backend.commit_message || ''}`
      : (error ? 'backend: version endpoint unreachable' : 'backend: loading…'),
    backend?.process_started_at ? `backend started: ${formatTimestamp(backend.process_started_at)}` : '',
    backend?.build_time ? `backend built: ${formatTimestamp(backend.build_time)}` : '',
  ].filter(Boolean);

  return (
    <span
      className={`inline-flex items-center gap-2 text-[11px] font-mono px-2 py-1 rounded border ${mismatch ? 'border-amber-500 text-amber-600 bg-amber-50' : 'border-muted text-muted-foreground bg-muted/30'} ${className}`}
      title={tooltipLines.join('\n')}
      data-testid="build-badge"
    >
      <span>fe:{FRONTEND_SHA}</span>
      <span className="opacity-60">·</span>
      <span>be:{backendSha}</span>
      {mismatch ? <span className="uppercase tracking-wide">mismatch</span> : null}
    </span>
  );
};

export default BuildBadge;
